import {extractIndoorMapLink} from "../util/util";
import GoogleMapLoader from "./map/GoogleMapLoader";
import CallHistory from "./CallHistory";
import React, {useCallback, useMemo, useState} from "react";

const Maps = ({topEmlLocation, callUuid, history}) => {
    const [alsEnabled, setAlsEnabled] = useState(true);
    const [updateLocations, setUpdateLocations] = useState(true);
    const [showAllLocations, setShowAllLocations] = useState(false);
    const [manualHistoryIndex, setManualHistoryIndex] = useState(null);

    const latestResolvedLocation = useMemo(() => resolveLocationForDisplay(history, 0, alsEnabled), [alsEnabled, history]);
    const latestPayloadForDisplay = latestResolvedLocation?.payload ?? topEmlLocation;
    const latestMarkerToDisplay = useMemo(() => latestResolvedLocation?.marker ?? null, [latestResolvedLocation]);
    const manualMarkerToDisplay = useMemo(() => {
        if (manualHistoryIndex === null) {
            return null;
        }

        return resolveLocationForDisplay(history, manualHistoryIndex, alsEnabled)?.marker ?? null;
    }, [alsEnabled, history, manualHistoryIndex]);
    const allMarkersToDisplay = useMemo(() => resolveAllMarkersForDisplay(history, alsEnabled), [alsEnabled, history]);

    let hrl = latestPayloadForDisplay.rankedLocations && latestPayloadForDisplay?.rankedLocations.length
        ? latestPayloadForDisplay?.rankedLocations[0].humanReadableLocation
        : "";
    hrl = hrl.replace(/(\n)+/g, "\n");
    let {parsedData} = extractIndoorMapLink(hrl);

    // Create a text representation for location checking
    const locationText = [
        parsedData.room,
        parsedData.floor,
        ...parsedData.otherLines
    ].filter(Boolean).join(" ").toLowerCase();

    const isEliOfficeLocation = locationText.indexOf("bedford") !== -1
        && locationText.indexOf("380") !== -1;
    const isTestLocation = locationText.indexOf("9200 corporate") !== -1;
    const isMYLocation = locationText.indexOf("manu tests") !== -1;
    const [activeTab, setActiveTab] = useState("LocationTab");

    const historyClickHandler = useCallback((historyItemIndex) =>
            // history starts from most recent but one
            setManualHistoryIndex(historyItemIndex + 1),
        []);

    const topHistoryItem = useMemo(() => [history[0]], [history]);
    const markerToDisplay = updateLocations
        ? latestMarkerToDisplay
        : (manualHistoryIndex === null ? latestMarkerToDisplay : manualMarkerToDisplay);

    return <div className="flex flex-col">
        <div className="w-screen h-screen max-w-full max-h-full z-5 flex flex-col">
            <GoogleMapLoader
                isEliOfficeLocation={isEliOfficeLocation}
                isTestLocation={isTestLocation}
                isMYLocation={isMYLocation}
                alsEnabled={alsEnabled}
                allLocationMarkers={allMarkersToDisplay}
                locationMarker={markerToDisplay}
                setAlsEnabled={setAlsEnabled}
                showAllLocations={showAllLocations}
                setShowAllLocations={setShowAllLocations}
                updateLocations={updateLocations}
                setUpdateLocations={setUpdateLocations}
            />
        </div>
        <div className="w-screenmax-w-md">
            <div className="flex border-b border-gray-300">
                <button
                    onClick={() => setActiveTab("LocationTab")}
                    className={`flex-1 py-2 px-4 text-center ${
                        activeTab === "LocationTab"
                            ? "border-b-2 border-blue-500 font-semibold text-blue-600"
                            : "text-gray-500"
                    }`}
                >
                    Location
                </button>
                <button
                    onClick={() => setActiveTab("HistoryTab")}
                    className={`flex-1 py-2 px-4 text-center ${
                        activeTab === "HistoryTab"
                            ? "border-b-2 border-blue-500 font-semibold text-blue-600"
                            : "text-gray-500"
                    }`}
                >
                    History
                </button>
            </div>
            <div className="p-4 border border-t-0 rounded-b shadow">
                {activeTab === "LocationTab" && <div>
                    <div className="flex flex-row overflow-y-auto max-h-50 bg-slate-200">
                        <CallHistory callUuid={callUuid}
                                     history={topHistoryItem}
                                     clickAllowed={false}/>
                    </div>
                </div>}
                {activeTab === "HistoryTab" && <div>
                    <CallHistory clickHandler={historyClickHandler}
                                 clickAllowed={!updateLocations}
                                 callUuid={callUuid}
                                 history={history.slice(1)}/>
                </div>}
                <div className="w-full flex flex-row">
                    <span
                        className="ml-auto m-2 p-2 max-w-fit font-medium text-sm text-slate-700 bg-blue-200 rounded-md">{history?.length} updates</span>
                </div>
            </div>
        </div>
    </div>
};

export default Maps;

function resolveLocationForDisplay(history = [], startIndex = 0, alsEnabled = true) {
    for (let i = startIndex; i < history.length; i += 1) {
        const payload = history[i]?.payload?.[0];
        const marker = getMarkerToDisplay(payload, alsEnabled);
        if (marker) {
            return {payload, marker};
        }
    }

    return null;
}

function getMarkerToDisplay(locationPayload, alsEnabled = true) {
    const rankedLocation = locationPayload?.rankedLocations?.[0];
    const dispatchableAddress = rankedLocation?.dispatchableAddress;

    if (!hasDispatchableAddress(dispatchableAddress)) {
        return null;
    }

    const useEstimatedGps = alsEnabled && locationPayload?.gpsEstimated;
    const lat = useEstimatedGps ? locationPayload?.gpsLatitude : rankedLocation?.tagGpsLatitude;
    const lng = useEstimatedGps ? locationPayload?.gpsLongitude : rankedLocation?.tagGpsLongitude;
    if (!hasCoordinate(lat) || !hasCoordinate(lng)) {
        return null;
    }

    return {
        lat,
        lng,
        type: "aps",
        id: "1",
        floor: dispatchableAddress.floor,
    };
}

function resolveAllMarkersForDisplay(history = [], alsEnabled = true) {
    return history.reduce((markers, historyItem, index) => {
        const payload = historyItem?.payload?.[0];
        const marker = getMarkerToDisplay(payload, alsEnabled);

        if (!marker) {
            return markers;
        }

        markers.push({
            ...marker,
            id: `history-${index}`,
            isLatest: index === 0,
            timestampLabel: String(index),
        });

        return markers;
    }, []);
}

function hasDispatchableAddress(dispatchableAddress) {
    return !!dispatchableAddress && Object.keys(dispatchableAddress).length > 0;
}

function hasCoordinate(value) {
    return value !== null && value !== undefined && value !== "";
}
